import window from 'window-or-global';
import { getOverride } from '@confluence/meta';
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';
import cloudAppPaste from './transformers/cloudapppaste';
import confluencePaste from './transformers/confluencepaste';
import googleRedirect from './transformers/google-redirect-convert';
import linkedIn from './transformers/linkedin';
import shortcutLinks from './transformers/shortcutlinks';
import skitchPaste from './transformers/skitchpaste';
import widgetPaste from './transformers/widgetpaste';
import youtube from './transformers/youtube';
import jiraPasteLink from './transformers/jiraPasteLink';
import jiraRoadmap from './transformers/jiraroadmap';
import twitterPaste from './transformers/twitter';
import portfolioforjiraplanPaste from './transformers/portfolioforjiraplan';

// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

// NOTE: match HTML attributes starting with `on` in order to sanitize pasted/dropped
// HTML fragments and prevent any possible XSS.
const sanitizeRegex = /\son[\w-]+(?=[^<]*>)/gi;

function initAutoConvert() {
	/***********
	 * Constructor
	 */
	const Autoconvert = function (ed) {
		const self = this;
		ed.on('Undo', (ev) => {
			self.cleanLinks(ed, ev);
		});
		this.editor = ed;
		this.weightedHandlers = [];
		this.handlers = [];

		$(window.document).on('prePaste', self.sanitizePaste);
	};

	/***********
	 * Instance Methods
	 */
	Autoconvert.prototype = {
		addHandler(handler, weight) {
			this.weightedHandlers.push({ handler, weight: weight || 0 });
			this.weightedHandlers.sort((a, b) => {
				return a.weight - b.weight;
			});
			this.handlers = [];
			const len = this.weightedHandlers.length;
			for (let i = 0; i < len; i++) {
				this.handlers.push(this.weightedHandlers[i].handler);
			}
		},
		cleanLinks(ed) {
			$('a.atlassian-autoconvert', ed.getDoc()).removeClass('atlassian-autoconvert');
			return true;
		},

		sanitizePaste(event, pl, data) {
			if (data && data.content) {
				data.content = data.content.replace(sanitizeRegex, (match) => ` unsafe-${match.trim()}`);
			}
		},

		callHandlers(n) {
			let index = 0;
			const self = this;
			const originalNode = $(n);
			let node = $(n).clone();
			const rawHref = node.attr('href');
			let uri = parseUri(hackAtSymbols(rawHref));

			/**
			 * Runs handlers one at a time, this method acts as a continuation and is passed to each handler.
			 */
			function run(newNode) {
				if (newNode && (newNode.length !== 1 || !newNode.attr('href'))) {
					return handlersFinished(newNode);
				}

				if (newNode) {
					node = newNode;
					index = 0;
					uri = parseUri(node.attr('href'));
					callHandler(self.handlers[index], uri, node);
				} else {
					index++;
					if (index < self.handlers.length) {
						callHandler(self.handlers[index], uri, node);
					} else {
						return handlersFinished(node);
					}
				}
			}

			/**
			 * This method ensures that a single handler can't break the event loop by never calling the continuation or by
			 * calling it multiple times.
			 */
			function callHandler(callback, uri, node) {
				let modifiableRun = run;
				let timer;

				const continuation = function () {
					const localIndex = index;
					clearTimeout(timer);

					//actually call the next iteration of run
					modifiableRun.apply(self, arguments);

					//ensure a single handler can't call continuation multiple times
					modifiableRun = function () {
						if (process.env.NODE_ENV !== 'production') {
							console.log(
								`Autoconvert: Callback #${localIndex} called the continuation multiple times.`,
							);
						}
					};
				};
				timer = setTimeout(() => {
					if (process.env.NODE_ENV !== 'production') {
						console.log(
							`Autoconvert: Callback #${index} failed to call continuation in time. If there is no subsequent log, the handler is improperly implemented and should be uninstalled.`,
						);
					}
					modifiableRun = function () {
						if (process.env.NODE_ENV !== 'production') {
							console.log(
								`Autoconvert: Callback #${index} did eventualy return. Probably a slow async call.`,
							);
						}
					};
					run();
				}, window.tinymce.plugins.Autoconvert.callbackTimeout);

				//Call the callback passing it a modified version of the continuation that allows us to prevent continuing after a timeout.
				callback(uri, node, continuation);
			}

			function handlersFinished(newNode) {
				self.editor.undoManager.beforeChange();
				const selection = self.editor.selection;
				const bm = self.editor.selection.getBookmark();

				//replace node that's actually in the editor with the newly modified node
				originalNode.replaceWith(newNode);

				selection.moveToBookmark(bm);
				self.editor.undoManager.add();
			}

			if (this.handlers.length !== 0) {
				callHandler(this.handlers[0], uri, node);
			}
		},
	};

	/***********
	 * Static properties
	 */
	Autoconvert.callbackTimeout = 5000;

	/***********
	 * Static methods
	 */
	Autoconvert.convertMacroToDom = function (macro, success, error) {
		const conversionData = {
			macro,
			contentId: getOverride('content-id'),
		};
		$.ajax({
			type: 'POST',
			contentType: 'application/json; charset=utf-8',
			url: `${CONTEXT_PATH}/rest/tinymce/1/macro/placeholder`,
			data: JSON.stringify(conversionData),
			dataType: 'text', // if this is switched back to json be sure to use "text json". See CONFDEV-4799 for details.
			success,
			error,
			timeout: 45000,
		});
	};

	Autoconvert.convertMacroToDomViaWikiText = function (macro, success, error) {
		const wikiTxt = ['{', macro.name];
		let firstParam = true;
		if (macro.defaultParameterValue) {
			wikiTxt.push(':', macro.defaultParameterValue);
			firstParam = false;
		}
		for (const key in macro.params) {
			if (firstParam) {
				wikiTxt.push(':');
				firstParam = false;
			} else {
				wikiTxt.push('|');
			}
			wikiTxt.push(key, '=', macro.params[key]);
		}
		wikiTxt.push('}');

		Autoconvert.getHtmlFromWikiMarkup(
			wikiTxt.join(''),
			(data) => {
				success($(data)[0]);
			},
			() => {
				error();
			},
		);
	};

	Autoconvert.getHtmlFromWikiMarkup = function (text, success, error) {
		const conversionData = {
			wiki: text,
			entityId: getOverride('content-id'),
			spaceKey: getOverride('space-key'),
		};
		$.ajax({
			type: 'POST',
			contentType: 'application/json; charset=utf-8',
			url: `${CONTEXT_PATH}/rest/tinymce/1/wikixhtmlconverter`,
			data: JSON.stringify(conversionData),
			dataType: 'text', // if this is switched back to json be sure to use "text json". See CONFDEV-4799 for details.
			success,
			error,
			timeout: 45000,
		});
	};

	/***********
	 * Private methods
	 */

	// parseUri 1.2.2
	// (c) Steven Levithan <stevenlevithan.com>
	// MIT License
	function parseUri(str) {
		const o = parseUri.options;
		const m = o.parser[o.strictMode ? 'strict' : 'loose'].exec(str);
		const uri = {};
		let i = 14;

		while (i--) {
			uri[o.key[i]] = m[i] || '';
		}

		uri[o.q.name] = {};
		uri[o.key[12]].replace(o.q.parser, ($0, $1, $2) => {
			if ($1) {
				uri[o.q.name][$1] = $2;
			}
		});

		return uri;
	}

	parseUri.options = {
		strictMode: false,
		key: [
			'source',
			'protocol',
			'authority',
			'userInfo',
			'user',
			'password',
			'host',
			'port',
			'relative',
			'path',
			'directory',
			'file',
			'query',
			'anchor',
		],
		q: {
			name: 'queryKey',
			parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
		},
		parser: {
			strict:
				/^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
			loose:
				/^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/,
		},
	};

	/**
	 * parseUri has a bug whereby it deems everything up to the first @ to be part of the "authority"
	 * section of the Uri. Even in strict mode.  We should fix this properly, but for now, we're just
	 * replacing the first @ with an encoded one.
	 */
	const hackAtSymbols = function (rawHref) {
		return rawHref.replace(/^(.*?\/\/[^/]*?\/.*?)(@)/, '$1%40');
	};

	// ======== TinyMCE plugin creation ===========================================================
	// Since this process relies on window.tinymce.plugins.AutoConvert already existing these two separate
	// concerns need to be included in the same source file. Otherwise you cannot create a new
	// window.tinymce.plugins.Autoconvert until TinyMCE is initialised, yet you cannot initialise this
	// TinyMCE plugin until window.tinymce.plugins.Autoconvert.autoConvert exists.

	const autoConvertPlugin = {
		init(ed) {
			const autoConvert = new Autoconvert(ed);

			// Need to refactored out for not to have this assignment here
			window.tinymce.plugins.Autoconvert.autoConvert = autoConvert;

			function postPasteHandler(e, pl, o) {
				const anchor = $(o.node).children('a');
				if (
					anchor.length === 1 &&
					anchor[0].tagName === 'A' &&
					anchor.text() === anchor.attr('href')
				) {
					anchor.attr('class', 'atlassian-autoconvert');
					setTimeout(() => {
						const anchor = $(
							'a.atlassian-autoconvert',
							$(ed.selection.getNode().ownerDocument),
						).removeClass('atlassian-autoconvert');
						autoConvert.callHandlers(anchor);
					}, 0);
				}
			}

			ed.on('init', () => {
				// Using jQuery is unfortunatelly necessary here. AE fires the event via jQuery which has it's own event-handling mechanism.
				// document.addEventListener() wouldn't work.
				$(document).on('postPaste', postPasteHandler);

				ed.addCommand('addAutoconverter', (ui, options) => {
					autoConvert.addHandler(options);
				});
			});

			ed.on('remove', () => {
				$(document).off('postPaste', postPasteHandler);
			});
		},

		getInfo() {
			return {
				longname: 'Atlassian Autoconvert',
				author: 'Atlassian',
				authorurl: 'http://www.atlassian.com',
				infourl: 'http://www.atlassian.com',
				version: '1.0',
			};
		},
	};

	return {
		autoConverter: Autoconvert,
		plugin: autoConvertPlugin,
	};
}

function addTransformers() {
	cloudAppPaste();
	confluencePaste();
	googleRedirect();
	linkedIn();
	shortcutLinks();
	skitchPaste();
	widgetPaste();
	youtube();
	jiraPasteLink();
	twitterPaste();
	jiraRoadmap();
	portfolioforjiraplanPaste();
}

export default function autoConvert() {
	const autoConvert = initAutoConvert();
	window.tinymce.plugins.Autoconvert = autoConvert.autoConverter;
	window.tinymce.create('tinymce.plugins.AutoconvertPlugin', autoConvert.plugin);
	window.tinymce.PluginManager.add('autoconvert', window.tinymce.plugins.AutoconvertPlugin);
	addTransformers();
}
